import { useEffect, useMemo, useState } from "react";
import { useDispatch } from "react-redux";
import { ArrowLeft, GitMerge, Loader2, Search, AlertTriangle } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import ConfirmDialog from "@/components/ui/ConfirmDialog";
import { setCurrentLeadId } from "@/features/leads/stores/leadFunctionalSlice";
import {
  useLeadMergePreview,
  useMergeLeadSearch,
  useMergeLeads,
  type LeadMergeChoices,
  type LeadMergePreview,
  type MergeLeadSide,
  type MergeSide,
  type NumberSlot,
} from "@/features/leads/api/useLeadMerge";

/**
 * MERGE LEAD (owner, 2026-10-19: "a proper merging option… one is whatsapp
 * number one is main number… ask which counsellor to choose").
 *
 * Step 1 — pick the duplicate. Step 2 — pick, field by field, what the merged
 * lead keeps (both leads side by side), the main + WhatsApp number, and who
 * keeps each role both leads have. Then confirm. The server moves everything
 * else and removes the other lead (`core/leads/merge/lead.merge.service.ts`).
 */
interface MergeLeadDialogProps {
  open: boolean;
  onClose: () => void;
  lead: { id: number; name?: string | null } | null;
}

const fmtDate = (v: string | null | undefined) =>
  v
    ? new Date(v).toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric", timeZone: "Asia/Kolkata" })
    : "";

const dash = (v: unknown) => (v == null || String(v).trim() === "" ? "—" : String(v));

const position = (l: MergeLeadSide) =>
  [l.stage?.name, l.status?.name, l.subStatus?.name].filter(Boolean).join(" › ") || "—";
const sourceOf = (l: MergeLeadSide) =>
  [l.source?.name, l.campaign?.name].filter(Boolean).join(" · ") || "—";

/** One selectable cell: a radio dot + value. */
function Choice({
  selected,
  onSelect,
  children,
  disabled,
  label,
}: {
  selected: boolean;
  onSelect: () => void;
  children: React.ReactNode;
  disabled?: boolean;
  label: string;
}) {
  return (
    <button
      type="button"
      role="radio"
      aria-checked={selected}
      aria-label={label}
      disabled={disabled}
      onClick={onSelect}
      className={`flex w-full min-w-0 items-start gap-2 rounded-lg border px-3 py-2 text-left text-sm transition-colors disabled:cursor-not-allowed disabled:opacity-50 ${
        selected ? "border-primary bg-primary/5" : "border-muted-foreground/20 hover:border-primary/50"
      }`}
    >
      <span
        className={`mt-1 h-3.5 w-3.5 shrink-0 rounded-full border ${
          selected ? "border-[4px] border-primary" : "border-muted-foreground/40"
        }`}
      />
      <span className="min-w-0 break-words">{children}</span>
    </button>
  );
}

function SideRow({
  label,
  field,
  choices,
  setSide,
  keepValue,
  otherValue,
}: {
  label: string;
  field: "name" | "email" | "location" | "branch" | "stage" | "source";
  choices: LeadMergeChoices;
  setSide: (field: "name" | "email" | "location" | "branch" | "stage" | "source", side: MergeSide) => void;
  keepValue: string;
  otherValue: string;
}) {
  return (
    <div className="grid gap-2 sm:grid-cols-[110px_1fr_1fr] sm:items-start">
      <p className="text-xs font-medium text-muted-foreground sm:pt-2.5">{label}</p>
      <div role="radiogroup" aria-label={label} className="contents">
        <Choice label={`${label}: ${keepValue}`} selected={choices[field] === "keep"} onSelect={() => setSide(field, "keep")}>
          {keepValue}
        </Choice>
        <Choice label={`${label}: ${otherValue}`} selected={choices[field] === "other"} onSelect={() => setSide(field, "other")}>
          {otherValue}
        </Choice>
      </div>
    </div>
  );
}

function PickStep({ excludeId, onPick }: { excludeId: number; onPick: (id: number) => void }) {
  const [search, setSearch] = useState("");
  const { leads, isSearching } = useMergeLeadSearch(search, excludeId);
  return (
    <div className="flex flex-col gap-3">
      <div className="relative">
        <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
        <input
          autoFocus
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Search the other lead by name or number"
          aria-label="Search the other lead"
          className="h-10 w-full rounded-lg border bg-white pl-9 pr-3 text-sm outline-none focus:border-primary dark:bg-transparent"
        />
      </div>
      <div className="flex max-h-[320px] flex-col gap-1 overflow-y-auto">
        {search.trim().length < 2 ? (
          <p className="px-1 py-6 text-center text-xs text-muted-foreground">Type at least 2 characters</p>
        ) : isSearching ? (
          <p className="flex items-center justify-center gap-2 py-6 text-xs text-muted-foreground">
            <Loader2 className="h-3.5 w-3.5 animate-spin" /> Searching…
          </p>
        ) : leads.length === 0 ? (
          <p className="px-1 py-6 text-center text-xs text-muted-foreground">No other lead matches</p>
        ) : (
          leads.map((l) => (
            <button
              key={l.id}
              type="button"
              onClick={() => onPick(l.id)}
              className="flex w-full min-w-0 flex-col items-start rounded-lg border border-transparent px-3 py-2 text-left hover:border-primary/40 hover:bg-primary/5"
            >
              <span className="text-sm font-medium">
                {l.name} <span className="font-normal text-muted-foreground">#{l.id}</span>
              </span>
              <span className="w-full truncate text-xs text-muted-foreground">
                {[l.phone, [l.stage, l.status].filter(Boolean).join(" › "), l.branch].filter(Boolean).join(" · ")}
              </span>
            </button>
          ))
        )}
      </div>
    </div>
  );
}

function ResolveStep({
  preview,
  choices,
  setChoices,
}: {
  preview: LeadMergePreview;
  choices: LeadMergeChoices;
  setChoices: (c: LeadMergeChoices) => void;
}) {
  const { keep, other } = preview;
  const setSide = (field: keyof LeadMergeChoices, side: MergeSide) => setChoices({ ...choices, [field]: side });
  const numbers = preview.numbers;
  const mismatch = preview.stageBranch[`${choices.stage}_${choices.branch}` as keyof LeadMergePreview["stageBranch"]];
  const dropped = numbers.filter((n) => n.slot !== choices.phone && n.slot !== choices.whatsapp);
  const contested = preview.roles.filter((r) => r.keep && r.other && r.keep.userId !== r.other.userId);
  const settled = preview.roles.filter((r) => !(r.keep && r.other && r.keep.userId !== r.other.userId));
  const moveParts = [
    [preview.moves.deals, "deal"],
    [preview.moves.notes, "note"],
    [preview.moves.followUps, "follow-up"],
    [preview.moves.calls, "call"],
    [preview.moves.chats, "chat"],
  ]
    .filter(([n]) => Number(n) > 0)
    .map(([n, w]) => `${n} ${w}${Number(n) > 1 ? "s" : ""}`);

  return (
    <div className="flex flex-col gap-5">
      <div className="hidden gap-2 text-xs font-medium sm:grid sm:grid-cols-[110px_1fr_1fr]">
        <span />
        <span>
          Kept lead #{keep.id} <span className="font-normal text-muted-foreground">· {fmtDate(keep.createdAt)}</span>
        </span>
        <span>
          Merged lead #{other.id} <span className="font-normal text-muted-foreground">· {fmtDate(other.createdAt)}</span>
        </span>
      </div>

      <section className="flex flex-col gap-2">
        <SideRow label="Name" field="name" choices={choices} setSide={setSide} keepValue={dash(keep.name)} otherValue={dash(other.name)} />
        <SideRow label="Email" field="email" choices={choices} setSide={setSide} keepValue={dash(keep.email)} otherValue={dash(other.email)} />
        <SideRow label="Location" field="location" choices={choices} setSide={setSide} keepValue={dash(keep.location)} otherValue={dash(other.location)} />
        <SideRow label="Source" field="source" choices={choices} setSide={setSide} keepValue={sourceOf(keep)} otherValue={sourceOf(other)} />
        <SideRow label="Branch" field="branch" choices={choices} setSide={setSide} keepValue={dash(keep.branch?.name)} otherValue={dash(other.branch?.name)} />
        <SideRow label="Stage" field="stage" choices={choices} setSide={setSide} keepValue={position(keep)} otherValue={position(other)} />
        {mismatch && (
          <p className="flex items-start gap-1.5 text-xs text-red-600">
            <AlertTriangle className="mt-0.5 h-3.5 w-3.5 shrink-0" /> {mismatch}
          </p>
        )}
      </section>

      <section className="grid gap-4 sm:grid-cols-2">
        <div className="flex flex-col gap-1.5" role="radiogroup" aria-label="Main number">
          <p className="text-xs font-medium">Main number</p>
          {numbers.map((n) => (
            <Choice
              key={n.slot}
              label={`Main number ${n.display}`}
              selected={choices.phone === n.slot}
              disabled={!n.valid && n.slot !== "keep.phone"}
              onSelect={() =>
                setChoices({ ...choices, phone: n.slot, whatsapp: choices.whatsapp === n.slot ? "none" : choices.whatsapp })
              }
            >
              {n.display} <span className="text-xs text-muted-foreground">· {n.slot.startsWith("keep") ? `#${keep.id}` : `#${other.id}`}</span>
            </Choice>
          ))}
        </div>
        <div className="flex flex-col gap-1.5" role="radiogroup" aria-label="WhatsApp number">
          <p className="text-xs font-medium">WhatsApp number</p>
          {numbers.map((n) => (
            <Choice
              key={n.slot}
              label={`WhatsApp number ${n.display}`}
              selected={choices.whatsapp === n.slot}
              disabled={choices.phone === n.slot || !n.valid}
              onSelect={() => setChoices({ ...choices, whatsapp: n.slot as NumberSlot })}
            >
              {n.display} <span className="text-xs text-muted-foreground">· {n.slot.startsWith("keep") ? `#${keep.id}` : `#${other.id}`}</span>
            </Choice>
          ))}
          <Choice label="No WhatsApp number" selected={choices.whatsapp === "none"} onSelect={() => setChoices({ ...choices, whatsapp: "none" })}>
            No separate WhatsApp number
          </Choice>
        </div>
        {dropped.length > 0 && (
          <p className="text-xs text-muted-foreground sm:col-span-2">
            Not kept: {dropped.map((d) => d.display).join(", ")} (recorded in the activity log)
          </p>
        )}
      </section>

      {preview.roles.length > 0 && (
        <section className="flex flex-col gap-2">
          <p className="text-xs font-medium">Who keeps the lead</p>
          {contested.map((r) => (
            <div key={r.roleId} className="grid gap-2 sm:grid-cols-[110px_1fr_1fr] sm:items-start" role="radiogroup" aria-label={r.roleName}>
              <p className="text-xs text-muted-foreground sm:pt-2.5">{r.roleName}</p>
              {(["keep", "other"] as const).map((side) => {
                const h = r[side]!;
                const fu = side === "keep" ? r.keepFollowUp : r.otherFollowUp;
                return (
                  <Choice
                    key={side}
                    label={`${r.roleName}: ${h.name ?? "Unnamed"}`}
                    selected={choices.holders[r.roleId] === h.userId}
                    onSelect={() => setChoices({ ...choices, holders: { ...choices.holders, [r.roleId]: h.userId } })}
                  >
                    {h.name ?? "Unnamed"}
                    {fu && <span className="block text-xs text-muted-foreground">Follow-up {fmtDate(fu)} is kept</span>}
                  </Choice>
                );
              })}
            </div>
          ))}
          {settled.length > 0 && (
            <p className="text-xs text-muted-foreground">
              Stays assigned:{" "}
              {settled
                .map((r) => `${(r.keep ?? r.other)?.name ?? "Unnamed"} (${r.roleName})`)
                .join(", ")}
            </p>
          )}
          {contested.length > 0 && (
            <p className="text-xs text-muted-foreground">
              The person not picked is unassigned from the merged lead; their follow-up on it is closed.
            </p>
          )}
        </section>
      )}

      <section className="rounded-lg bg-muted/40 px-3 py-2.5 text-xs text-muted-foreground">
        {moveParts.length > 0 ? `${moveParts.join(", ")}, ` : ""}notes, files, custom fields and the activity history of #{other.id} move into
        #{keep.id}. WhatsApp chats on the same line become one conversation. Custom fields keep #{keep.id}'s values and fill
        the empty ones from #{other.id}. The merged lead takes the earlier Created At date.
      </section>
    </div>
  );
}

export default function MergeLeadDialog({ open, onClose, lead }: MergeLeadDialogProps) {
  const dispatch = useDispatch();
  const currentId = lead?.id ?? null;
  const [otherId, setOtherId] = useState<number | null>(null);
  // Which lead's id survives: the open lead by default.
  const [keepCurrent, setKeepCurrent] = useState(true);
  const [choices, setChoices] = useState<LeadMergeChoices | null>(null);
  const [confirming, setConfirming] = useState(false);

  const keepId = otherId == null ? null : keepCurrent ? currentId : otherId;
  const mergedId = otherId == null ? null : keepCurrent ? otherId : currentId;
  const previewQuery = useLeadMergePreview(keepId, mergedId);
  const preview = previewQuery.data;
  const merge = useMergeLeads();

  useEffect(() => {
    if (!open) {
      setOtherId(null);
      setKeepCurrent(true);
      setChoices(null);
      setConfirming(false);
    }
  }, [open]);

  // Fresh defaults whenever the pair (or which one is kept) changes.
  useEffect(() => {
    setChoices(preview ? preview.defaults : null);
  }, [preview]);

  const mismatch = useMemo(
    () =>
      preview && choices
        ? preview.stageBranch[`${choices.stage}_${choices.branch}` as keyof LeadMergePreview["stageBranch"]]
        : null,
    [preview, choices],
  );

  const runMerge = async () => {
    if (keepId == null || mergedId == null || !choices) return;
    try {
      const res = await merge.mutateAsync({ keepId, otherId: mergedId, choices });
      setConfirming(false);
      onClose();
      // The open lead was the one removed → show the merged lead instead.
      if (res.leadId !== currentId) dispatch(setCurrentLeadId(res.leadId));
    } catch {
      setConfirming(false);
    }
  };

  const errorMessage = (previewQuery.error as any)?.response?.data?.message;

  return (
    <>
      <Dialog open={open} onOpenChange={(o) => !o && !merge.isPending && onClose()}>
        <DialogContent className="max-h-[88vh] overflow-y-auto sm:max-w-3xl">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <GitMerge className="h-4 w-4" /> Merge lead
            </DialogTitle>
            <DialogDescription>
              {otherId == null
                ? `Pick the duplicate of ${lead?.name ?? "this lead"}. Its data moves into one lead and the other is removed.`
                : "Pick what the merged lead keeps. Everything else on both leads is combined."}
            </DialogDescription>
          </DialogHeader>

          {otherId == null || currentId == null ? (
            currentId != null && <PickStep excludeId={currentId} onPick={setOtherId} />
          ) : (
            <div className="flex flex-col gap-4">
              <div className="flex flex-col gap-1.5" role="radiogroup" aria-label="Which lead to keep">
                <p className="text-xs font-medium">Keep which lead's ID and history?</p>
                <div className="grid gap-2 sm:grid-cols-2">
                  {[true, false].map((keepsCurrent) => {
                    const side = preview ? (keepsCurrent === keepCurrent ? preview.keep : preview.other) : null;
                    const id = keepsCurrent ? currentId : otherId;
                    return (
                      <Choice
                        key={String(keepsCurrent)}
                        label={`Keep lead #${id}`}
                        selected={keepCurrent === keepsCurrent}
                        disabled={merge.isPending}
                        onSelect={() => setKeepCurrent(keepsCurrent)}
                      >
                        <span className="font-medium">
                          #{id} {side?.name ?? ""}
                        </span>
                        {side && <span className="block text-xs text-muted-foreground">Created {fmtDate(side.createdAt)}</span>}
                      </Choice>
                    );
                  })}
                </div>
              </div>

              {previewQuery.isLoading || (preview && !choices) ? (
                <p className="flex items-center justify-center gap-2 py-10 text-sm text-muted-foreground">
                  <Loader2 className="h-4 w-4 animate-spin" /> Loading both leads…
                </p>
              ) : previewQuery.isError ? (
                <p className="rounded-lg border border-red-200 bg-red-50 px-3 py-2.5 text-sm text-red-700 dark:border-red-900 dark:bg-red-950 dark:text-red-300">
                  {errorMessage || "Couldn't load these leads."}
                </p>
              ) : preview && choices ? (
                <ResolveStep preview={preview} choices={choices} setChoices={setChoices} />
              ) : null}
            </div>
          )}

          <DialogFooter className="gap-2 sm:justify-between">
            {otherId != null ? (
              <Button variant="cancel" onClick={() => setOtherId(null)} disabled={merge.isPending}>
                <ArrowLeft className="mr-1 h-4 w-4" /> Pick another lead
              </Button>
            ) : (
              <span />
            )}
            <div className="flex gap-2">
              <Button variant="cancel" onClick={onClose} disabled={merge.isPending}>
                Cancel
              </Button>
              {otherId != null && (
                <Button
                  variant="destructive"
                  disabled={!preview || !choices || Boolean(mismatch) || merge.isPending}
                  onClick={() => setConfirming(true)}
                >
                  Merge leads
                </Button>
              )}
            </div>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <ConfirmDialog
        open={confirming}
        onClose={() => !merge.isPending && setConfirming(false)}
        onConfirm={runMerge}
        title={`Merge #${mergedId} into #${keepId}?`}
        tone="destructive"
        warningTitle="This can't be undone"
        warningDescription={`Lead #${mergedId} is removed after everything on it moves into #${keepId}.`}
        confirmLabel="Merge leads"
        loadingLabel="Merging…"
        isLoading={merge.isPending}
      />
    </>
  );
}
